import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";
import { motion, AnimatePresence } from "framer-motion";
import { AvatarUpload } from "@/components/profile/AvatarUpload";

interface NameStepProps {
  onNext: (data: { name: string }) => void;
  initialData: { email?: string };
}

export const NameStep = ({ onNext, initialData }: NameStepProps) => {
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const canSubmit = name.trim().length >= 2;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not found');
      }

      // Validação extra: garantir que name não é vazio
      const trimmedName = name.trim();
      if (!trimmedName || trimmedName.length < 2) {
        throw new Error('Nome deve ter pelo menos 2 caracteres');
      }

      logger.info('Updating user profile with name', { userId: user.id });

      // Buscar perfil existente para preservar dados
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Criar/atualizar profile com dados mínimos, preservando o que já existe
      const profileCompletion = avatarUrl 
        ? Math.max(existingProfile?.profile_completion || 0, 50)
        : Math.max(existingProfile?.profile_completion || 0, 30);

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          name: trimmedName,
          avatar_url: avatarUrl || existingProfile?.avatar_url || null,
          onboarding_completed: true,
          force_profile_completion: false,
          profile_completion: profileCompletion,
          completed_sections: {
            ...(typeof existingProfile?.completed_sections === 'object' && existingProfile?.completed_sections !== null 
              ? existingProfile.completed_sections 
              : {}
            ),
            basic: true,
          }
        }, {
          onConflict: 'id'
        });

      if (profileError) throw profileError;

      logger.info('Profile created successfully', { userId: user.id });

      toast({
        title: "Bem-vindo!",
        description: "Sua conta foi criada com sucesso.",
      });

      // Navegar para dashboard
      navigate('/dashboard');
    } catch (error: any) {
      logger.error('Failed to create profile', error);
      toast({
        variant: "destructive",
        title: "Erro ao criar perfil",
        description: "Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h2 className="text-2xl font-bold">Complete seu perfil</h2>
        <p className="text-muted-foreground">
          Adicione seu nome e foto para começar sua jornada
        </p>
      </motion.div>

      {/* Avatar Upload */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        <AvatarUpload
          currentAvatarUrl={avatarUrl}
          userName={name}
          onUploadComplete={setAvatarUrl}
          compact
        />
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Nome Completo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <Label htmlFor="name">
            Nome Completo <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              id="name"
              type="text"
              placeholder="Maria Silva"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              autoFocus
              className="pr-10 transition-all duration-200"
              autoComplete="name"
            />
            <AnimatePresence>
              {name.trim().length >= 2 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <CheckCircle2 className="w-4 h-4 text-success" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <p className="text-xs text-muted-foreground">
            Nome que aparecerá em certificados
          </p>
        </motion.div>

        {/* Submit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            type="submit"
            className="w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            disabled={!canSubmit || loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                />
                Finalizando...
              </span>
            ) : avatarUrl ? (
              "Começar minha jornada! 🏃‍♀️"
            ) : (
              "Continuar"
            )}
          </Button>
        </motion.div>
      </form>
    </div>
  );
};